import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  schemaJsonLd?: Record<string, unknown>[];
}

export default function SEOHead({ title, description, keywords, canonicalUrl, ogType = 'website', schemaJsonLd }: SEOHeadProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const metaTags: Record<string, { selector: string; attribute: string }> = {
      description: { selector: 'meta[name="description"]', attribute: 'content' },
      keywords: { selector: 'meta[name="keywords"]', attribute: 'content' },
      'og:title': { selector: 'meta[property="og:title"]', attribute: 'content' },
      'og:description': { selector: 'meta[property="og:description"]', attribute: 'content' },
      'og:url': { selector: 'meta[property="og:url"]', attribute: 'content' },
      'og:type': { selector: 'meta[property="og:type"]', attribute: 'content' },
      'twitter:title': { selector: 'meta[name="twitter:title"]', attribute: 'content' },
      'twitter:description': { selector: 'meta[name="twitter:description"]', attribute: 'content' },
    };

    const updates: Record<string, string> = {
      description,
      keywords,
      'og:title': title,
      'og:description': description,
      'og:url': canonicalUrl,
      'og:type': ogType,
      'twitter:title': title,
      'twitter:description': description,
    };

    Object.entries(updates).forEach(([key, value]) => {
      const meta = metaTags[key];
      if (meta) {
        const el = document.querySelector(meta.selector);
        if (el) {
          el.setAttribute(meta.attribute, value);
        }
      }
    });

    // Update canonical
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonicalUrl);
    }

    // Inject JSON-LD schemas
    const existingScripts = document.querySelectorAll('script[data-dynamic-schema]');
    existingScripts.forEach(s => s.remove());

    if (schemaJsonLd && schemaJsonLd.length > 0) {
      schemaJsonLd.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-dynamic-schema', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      const cleanupScripts = document.querySelectorAll('script[data-dynamic-schema]');
      cleanupScripts.forEach(s => s.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogType, schemaJsonLd]);

  return null;
}